// ============================================================
// TMR Portal Admin — como o molde global se lê na tela
// ============================================================
import { rateTypeLabel } from '../rateFormat';
import type { TmrRate, TmrRateImpact } from './contract';

/**
 * What the template line is for, as a line the operator recognizes.
 *
 * A type the catalogue could not name falls back to its id rather than to nothing — the id is at least
 * something to search the legacy with, and the catalogue has been wrong before: its static fallback was
 * missing `tipo_taxa` 1 entirely, a type with 3.261 rows in production (`15` §10, D-A10).
 *
 * @param rate The template line.
 */
export function tmrRateTitle(rate: TmrRate): string {
  return rate.rateTypeName ? rateTypeLabel(rate.rateTypeName) : `Tipo ${rate.rateTypeId}`;
}

/**
 * Whether the legacy is out of step with what this screen shows for a template line.
 *
 * False for a line that has never been replicated and for one whose export has not run yet — either way the
 * number on screen is not the number `taxas_tmr` holds, and it is `taxas_tmr` that a new Empresa is seeded
 * from.
 *
 * @param rate The template line.
 */
export function isAwaitingLegacy(rate: TmrRate): boolean {
  return !rate.legacySynced;
}

/**
 * The blast radius of a propagation, in words — the sentence shown beside the choice, not after it.
 *
 * Zero is a real and useful answer: it means the change is safe to propagate because nothing out there
 * matches this line's natural key. `legacyAvailable: false` is a different answer again, and must never be
 * read as zero — the counts are then unknown.
 *
 * @param impact What the server counted.
 */
export function impactSummary(impact: TmrRateImpact): string {
  if (!impact.legacyAvailable) {
    return 'Não foi possível contar no sistema legado agora, então não dá para dizer quantas empresas seriam alcançadas.';
  }

  if (impact.companies === 0) {
    return 'Nenhuma empresa tem uma taxa com este tipo e faixa — propagar não muda nada hoje.';
  }

  const companies = impact.companies === 1 ? '1 empresa' : `${impact.companies} empresas`;
  const launches = impact.launches === 1 ? '1 lançamento' : `${impact.launches} lançamentos`;

  return `${companies} e ${launches} passam a ser cobrados por este valor.`;
}

/**
 * The Empresas a propagation would leave out, when there are any.
 *
 * Today this is zero everywhere — the 1.115 producers all hold the template (`15`, dimensionamento b). It
 * is shown anyway because the legacy's broken fan-out (`AtualizarTaxasProdutor`, whose deduplication omits
 * `produtor_id`) can produce Empresas without a template again for as long as it is alive.
 *
 * @param impact What the server counted.
 */
export function missingTemplateWarning(impact: TmrRateImpact): string | null {
  if (!impact.legacyAvailable || impact.companiesWithoutTemplate === 0) return null;

  return impact.companiesWithoutTemplate === 1
    ? '1 empresa não tem nenhuma taxa com este tipo e faixa, e continua sem — a propagação atualiza, não cria.'
    : `${impact.companiesWithoutTemplate} empresas não têm nenhuma taxa com este tipo e faixa, e continuam sem — a propagação atualiza, não cria.`;
}

/**
 * What to tell the operator after a save, once the modal is gone.
 *
 * A propagation is **enqueued**, not applied: the write goes into the outbox as its own event and a worker
 * carries it into the legacy, so that a propagation touching a thousand rows cannot drag the one-row
 * template change back with it on failure (`15` §12, 2026-08-14). The wording says "enfileirada" for that
 * reason — the plan's original "progresso por empresa" would need a status endpoint that does not exist,
 * and inventing one on screen would report a completion nobody observed.
 *
 * @param propagated Whether the operator chose to propagate.
 * @param impact What the propagation was counted to reach, when it could be counted.
 */
export function saveSummary(propagated: boolean, impact: TmrRateImpact | null): string {
  if (!propagated) {
    return 'Molde atualizado. As empresas que já existem seguem com o valor que tinham; o novo vale para as próximas.';
  }

  if (!impact || !impact.legacyAvailable) {
    return 'Molde atualizado e propagação enfileirada — as empresas alcançadas recebem o novo valor assim que a fila for processada.';
  }

  const companies = impact.companies === 1 ? '1 empresa' : `${impact.companies} empresas`;
  const launches = impact.launches === 1 ? '1 lançamento' : `${impact.launches} lançamentos`;

  return `Molde atualizado e propagação enfileirada para ${companies} e ${launches} — o novo valor chega ao sistema legado assim que a fila for processada.`;
}
